import { Head, Link, useForm } from "@inertiajs/react"
import AuthLayout from "@/components/auth/layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { track } from "@/lib/analytics"
import { csrfToken } from "@/lib/csrf"

export default function SignIn() {
  const { data, setData, post, processing, errors, transform } = useForm({
    email: "",
    password: "",
    remember: false,
  })
  transform((d) => ({
    user: {
      email: d.email,
      password: d.password,
      remember_me: d.remember ? "1" : "0",
    },
  }))

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    track("user_signed_in", { method: "email" })
    post("/sign-in")
  }

  return (
    <>
      <Head title="Sign In | QuiteRight" />
      <AuthLayout heading="Welcome back" subheading="Sign in to your account.">
        {/* Google OAuth */}
        <form
          method="post"
          action="/auth/google_oauth2"
          className="mb-6"
          onSubmit={(e) => {
            const input = e.currentTarget.querySelector<HTMLInputElement>('input[name="authenticity_token"]')
            if (input) input.value = csrfToken()
            track("user_signed_in", { method: "google" })
          }}
        >
          <input type="hidden" name="authenticity_token" defaultValue={csrfToken()} />
          <Button
            type="submit"
            variant="outline"
            className="w-full gap-3 border-stone/60 bg-white py-5 text-sm font-medium text-charcoal hover:bg-stone/20"
          >
            <GoogleIcon />
            Continue with Google
          </Button>
        </form>

        <div className="relative mb-6">
          <Separator className="bg-stone/60" />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-xs text-warm-grey">
            or
          </span>
        </div>

        {/* Email form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              value={data.email}
              onChange={(e) => setData("email", e.target.value)}
              placeholder="jane@example.co.uk"
              className="mt-1.5"
              autoComplete="email"
              required
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red">{errors.email}</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link
                href="/password/new"
                className="text-xs text-warm-grey hover:text-charcoal"
              >
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              value={data.password}
              onChange={(e) => setData("password", e.target.value)}
              className="mt-1.5"
              autoComplete="current-password"
              required
            />
            {errors.password && (
              <p className="mt-1 text-xs text-red">{errors.password}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              id="remember"
              type="checkbox"
              checked={data.remember}
              onChange={(e) => setData("remember", e.target.checked)}
              className="size-4 rounded border-stone/60 text-red focus:ring-red/30"
            />
            <Label htmlFor="remember" className="text-sm font-normal">
              Remember me
            </Label>
          </div>

          <Button
            type="submit"
            disabled={processing}
            className="w-full bg-red py-5 text-sm font-semibold text-white hover:bg-red-deep"
          >
            {processing ? "Signing in..." : "Sign in"}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-warm-grey">
          Interested in QuiteRight?{" "}
          <Link
            href="/demo"
            className="font-medium text-charcoal underline-offset-4 hover:underline"
          >
            Request a demo
          </Link>
        </p>
      </AuthLayout>
    </>
  )
}

function GoogleIcon() {
  return (
    <svg className="size-5" viewBox="0 0 24 24">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  )
}
